import { useEffect, useMemo, useRef } from "react";
import type { Analysis, Span } from "../lib/analysis";
import { type ChordView, display, isNoChord } from "../lib/chords";

/**
 * The chart: chords set above the words they land on.
 *
 * Three decisions here were made against measurements rather than taste, and
 * are worth keeping when this is edited:
 *
 *  - A chord attaches to the NEAREST placement point in time, not to whichever
 *    word's span contains it. Sung onsets scatter about a quarter-beat around
 *    the grid, so a containment test puts chords a word early surprisingly
 *    often.
 *  - Placement points are syllables where the aligner resolved them, so a chord
 *    can sit inside a long word instead of being pushed to its start.
 *  - Passages with no singing — the intro, a solo, the outro — get their own
 *    rows. They are in the analysis; before this they were simply never drawn,
 *    which lost a 44-second intro on the first song anybody checked.
 */

const INSTRUMENTAL_GAP = 6;  // silence long enough to be a section of its own
// How long after the last syllable a chord can still belong to it. Beyond
// this the singing has stopped and the chord belongs to the gap, not to the
// final word.
const WORD_TAIL = 1.2;
// A chord this close before a line's first word is the pickup INTO that
// phrase and opens it. Anything earlier in the gap is the turnaround at the
// end of the line before. Swept against a human transcription: 0.7 puts 5 of
// 6 verse lines exactly right; 1.5 loses every trailing chord to the next line.
const PICKUP = 0.7;
// Furthest a chord may sit from a syllable and still be printed over it.


export interface Row {
  kind: "lyric" | "instrumental";
  from: number;
  to: number;
  label?: string;            // Intro / Instrumental / Outro
  chordRow: string;
  marks: { column: number; label: string; beat: number; endBeat: number }[];
  words: { text: string; t: number }[];
}

export function buildRows(analysis: Analysis, view: ChordView): Row[] {
  const changes = analysis.chords.filter((c) => !isNoChord(c.label));
  const lines = analysis.lyrics.lines;
  const rows: Row[] = [];

  const instrumental = (from: number, to: number, label: string) => {
    const played = changes.filter((c) => c.start >= from - 0.15 && c.start < to);
    if (played.length < 2 || to - from < 3) return;
    const marks: Row["marks"] = [];
    let column = 0;
    for (const chord of played) {
      const text = display(chord.label, view);
      marks.push({ column, label: text, beat: chord.start_beat,
                    endBeat: chord.end_beat });
      column += text.length + 3;
    }
    rows.push({
      kind: "instrumental", from, to, label,
      chordRow: played.map((c) => display(c.label, view)).join("   "),
      marks, words: [],
    });
  };

  // Every placement point in the song, flat, each remembering which line it
  // belongs to. A chord must be able to find its nearest column even when that
  // column is on the NEXT line: the chord opening a phrase sounds before its
  // first word, and comparing only within one line can never see that — it
  // lands on the previous line's last word instead, which is where every
  // opening chord was going.
  const laid = lines.map((line) => {
    const words = line.words.length ? line.words : [{ t: line.t, w: line.text }];
    const points: { t: number; column: number }[] = [];
    const rendered: { text: string; t: number }[] = [];
    let column = 0;
    for (const word of words) {
      const parts = word.syl?.length ? word.syl : ([[0, word.t]] as [number, number][]);
      for (const [offset, at] of parts) points.push({ t: at, column: column + offset });
      rendered.push({ text: word.w, t: word.t });
      column += word.w.length + 1;
    }
    const lastPoint = points.length ? Math.max(...points.map((q) => q.t)) : line.t;
    return { line, points, rendered, lastPoint, width: column };
  });

  const placed = laid.map(() => new Map<number, Span[]>());
  const orphans: typeof changes = [];
  const firstOf = (i: number) => laid[i].points[0]?.t ?? laid[i].line.t;
  const lastOf = (i: number) => laid[i].lastPoint;

  const put = (row: number, column: number, chord: Span) => {
    const columns = placed[row];
    columns.set(column, [...(columns.get(column) ?? []), chord]);
  };

  for (const chord of changes) {
    const t = chord.start;

    // Inside a sung line: nearest placement point wins. Containment would put
    // the chord a word early, because sung onsets scatter around the grid.
    const inside = laid.findIndex((l) => t >= firstOf(laid.indexOf(l)) - 0.05
                                      && t <= l.lastPoint + 0.05);
    if (inside >= 0) {
      let best = laid[inside].points[0], gap = Infinity;
      for (const point of laid[inside].points) {
        const distance = Math.abs(point.t - t);
        if (distance < gap) { gap = distance; best = point; }
      }
      put(inside, best?.column ?? 0, chord);
      continue;
    }

    // In a gap: the pickup opens the next phrase, the turnaround closes the last.
    const after = laid.findIndex((l, i) => firstOf(i) > t);
    const before = after > 0 ? after - 1 : (after === -1 ? laid.length - 1 : -1);
    if (after >= 0 && firstOf(after) - t <= PICKUP) {
      put(after, 0, chord);
    } else if (before >= 0 && after >= 0 && firstOf(after) - lastOf(before) <= INSTRUMENTAL_GAP) {
      put(before, laid[before].width, chord);      // trailing, after the words
    } else {
      orphans.push(chord);
    }
  }

  const renderRow = (index: number): Row => {
    const { line, rendered, lastPoint } = laid[index];
    const columns = placed[index];

    // A chord still ringing when the line opens, when nothing else opens it.
    if (!columns.has(0)) {
      const held = changes.filter((c) => c.start < line.t && c.end > line.t).slice(-1);
      if (held.length) columns.set(0, held);
    }

    const marks: Row["marks"] = [];
    let filled = 0;                       // rightmost column already occupied
    for (const key of [...columns.keys()].sort((a, b) => a - b)) {
      const here = columns.get(key)!;
      const label = [...new Set(here.map((c) => display(c.label, view)))].join(" ");
      const column = Math.max(key, filled);
      marks.push({ column, label, beat: here[0].start_beat,
                   endBeat: here[here.length - 1].end_beat });
      filled = column + label.length + 1;
    }
    const chordRow = marks.reduce(
      (line, mark) => line.padEnd(mark.column) + mark.label, "");
    return { kind: "lyric", from: line.t, to: lastPoint + WORD_TAIL,
             chordRow, marks, words: rendered };
  };

  if (lines.length) instrumental(0, lines[0].t, "Intro");

  for (let index = 0; index < laid.length; index += 1) {
    const previous = index > 0 ? laid[index - 1].lastPoint + WORD_TAIL : undefined;
    const here = laid[index].line.t;
    if (previous !== undefined && here - previous > INSTRUMENTAL_GAP) {
      const between = orphans.filter((c) => c.start >= previous - 0.15 && c.start < here);
      if (between.length >= 2) {
        const marks: Row["marks"] = [];
        let column = 0;
        for (const chord of between) {
          const text = display(chord.label, view);
          marks.push({ column, label: text, beat: chord.start_beat,
                    endBeat: chord.end_beat });
          column += text.length + 3;
        }
        rows.push({
          kind: "instrumental", from: previous, to: here, label: "Instrumental",
          chordRow: between.map((c) => display(c.label, view)).join("   "),
          marks, words: [],
        });
      }
    }
    rows.push(renderRow(index));
  }

  if (lines.length) {
    const last = laid[laid.length - 1];
    instrumental(last.lastPoint + WORD_TAIL, analysis.duration, "Outro");
  }

  // No words at all: print the grid rather than an empty page.
  if (!lines.length && changes.length) {
    instrumental(0, analysis.duration, "Chords");
  }
  return rows;
}

export function ChordChart({
  analysis, view, currentTime, onSeek, onEditChord, follow, rows: given,
}: {
  analysis: Analysis;
  /** An authored chart, when the song has one. It replaces the derived layout
   *  entirely: once someone has edited a song, what they typed is the chart. */
  rows?: Row[];
  view: ChordView;
  currentTime: number;
  onSeek?: (seconds: number) => void;
  /** Given the beat a chord starts on, offer to fix it. */
  onEditChord?: (span: { beat: number; endBeat: number; label: string }) => void;
  /** Hold the line being sung in the middle of the screen. */
  follow?: boolean;
}) {
  const derived = useMemo(() => buildRows(analysis, view), [analysis, view]);
  const rows = given ?? derived;
  const activeIndex = rows.reduce(
    (active, row, index) => (row.from <= currentTime ? index : active), -1);

  // Keep the line being sung in the middle of the screen, so what comes next is
  // already visible — a chart that scrolls a line at a time is useless on a
  // stand, because the line you need is at the bottom edge when you need it.
  const lines = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    if (!follow || activeIndex < 0) return;
    const element = lines.current[activeIndex];
    if (!element) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    element.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" });
  }, [follow, activeIndex]);

  return (
    <div className="chart">
      {rows.map((row, index) => (
        <div
          key={`${row.kind}-${row.from}-${index}`}
          ref={(element) => { lines.current[index] = element; }}
          className={[
            "section",
            row.kind === "instrumental" ? "instrumental" : "lyric",
            index === activeIndex ? "now" : "",
          ].join(" ")}
          onClick={() => onSeek?.(row.from)}
        >
          {row.label && <span className="section-label mono">{row.label}</span>}
          {row.marks.length > 0 && (
            <div className="chords mono">
              {row.marks.map((mark) => (
                onEditChord ? (
                  <button
                    key={`${mark.beat}-${mark.column}`}
                    type="button"
                    className="chord-mark"
                    style={{ left: `${mark.column}ch` }}
                    title="Fix this chord"
                    onClick={(event) => {
                      event.stopPropagation();      // do not also seek
                      onEditChord({ beat: mark.beat, endBeat: mark.endBeat, label: mark.label });
                    }}
                  >{mark.label}</button>
                ) : (
                  <span
                    key={`${mark.beat}-${mark.column}`}
                    className="chord-mark"
                    style={{ left: `${mark.column}ch` }}
                  >{mark.label}</span>
                )
              ))}
            </div>
          )}
          {row.kind === "lyric" && (
            <div className="words">
              {row.words.map((word, position) => (
                <span key={position} className={word.t <= currentTime ? "w sung" : "w"}>
                  {word.text}{" "}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
